import { useDepartmentText } from 'model/DepartmentTerminology';
import { Group, Modal } from '@mantine/core';
import { DepartmentListResponse } from 'api/actions/department-list/department-list-response';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { useEffect, useMemo, useState } from 'react';
import { DepartmentSelect } from 'components/selects/DepartmentSelect';
import MultiCheckbox, { MultiCheckboxItem } from 'components/inputs/multi-checkbox/MultiCheckbox';
import BadgeDeleted from 'components/Badge';
import useDepartmentUiMode from 'model/useDepartmentUiMode';
import { isEmpty, noop } from 'lodash';
import Button from 'components/Button';

/**
 * A single department.
 */
export type PickDepartmentItem = DepartmentListResponse[number];

/**
 * Parameters of the PickDepartments component.
 */
export interface PickDepartmentsParams {
  opened?: boolean;
  title?: string;
  initialPicked?: number[];
  excludeIds?: number[];
  showDiscarded?: boolean;
  permissionSlug?: string;
  onClose?: () => void;
  onPick?: (departments: PickDepartmentItem[]) => void;
  onLoad?: (departments: PickDepartmentItem[]) => void;
}

/**
 * Modal for selecting departments.
 */
export default function PickDepartments({
  opened = false,
  title,
  initialPicked = [],
  excludeIds = [],
  showDiscarded = false,
  permissionSlug = undefined,
  onClose = noop,
  onPick = noop,
  onLoad = noop,
}: PickDepartmentsParams = {}) {
  const departmentText = useDepartmentText();
  const { mode } = useDepartmentUiMode();
  const { getAction } = useApi();
  const [departments, setDepartments] = useState<PickDepartmentItem[]>([]);
  const [picked, setPicked] = useState<number[]>(initialPicked);
  const [loading, setLoading] = useState(true);
  const flatOptions = useMemo<MultiCheckboxItem[]>(
    () =>
      departments.map(({ departmentId, departmentName, status, organization }) => ({
        value: departmentId,
        label: departmentName,
        iconLeft: status ? null : <BadgeDeleted size="sm" text="Vyradené" />,
        group: organization.organizationName,
      })),
    [departments]
  );

  // Fetch departments from the API.
  useEffect(() => {
    setLoading(true);

    const departmentGetListAction = getAction('DepartmentList');

    departmentGetListAction({
      query: { filters: { 'showDiscarded.eq': showDiscarded ? 1 : 0, 'permissionSlug.eq': permissionSlug } },
    })
      .then((loadedDepartments) => {
        setDepartments(loadedDepartments);
        onLoad(loadedDepartments);
      })
      .catch(panic)
      .finally(() => setLoading(false));
  }, []);

  /**
   * Is called when the user clicks on the "Pridať" button.
   */
  const onPickImpl = () => {
    onPick(departments.filter(({ departmentId }) => picked.includes(departmentId)));
    setPicked([]);
    onClose();
  };

  return (
    <Modal
      title={title ?? departmentText('Pridať položku úrovne', 'Pridať stredisko')}
      opened={opened}
      onClose={onClose}
      size="xl"
    >
      {mode === 'legacy' ? (
        <MultiCheckbox
          withSearch
          data={flatOptions}
          value={picked}
          loading={loading}
          onChange={setPicked}
          excludeIds={excludeIds}
          group
          searchPlaceholder="Hľadať strediská"
          noResultPlaceholder="Neboli nájdené žiadne strediská."
        />
      ) : (
        <DepartmentSelect
          multiple
          inline
          confirmSelection={false}
          selectSubtrees
          pruneCoveredIds
          excludeSubtrees
          excludedIds={excludeIds}
          groupByOrganization
          preloadedDepartments={departments}
          loading={loading}
          emptyText="Neboli nájdené žiadne záznamy."
          value={picked.map(String)}
          onChange={(ids) => setPicked(ids.map(Number))}
        />
      )}
      <Group position="apart" pt={24}>
        <Button size="md" variant="subtle-gray" onClick={onClose}>
          Zrušiť
        </Button>
        <Button size="md" variant="primary" onClick={onPickImpl} disabled={isEmpty(picked)}>
          Pridať
        </Button>
      </Group>
    </Modal>
  );
}
